import { useEffect, useMemo, useState } from "react"

import { Flex, Spin } from "antd"
import { useDispatch, useSelector } from "react-redux"

import SelectComponent from "../../../../../Components/Select"
import Images from "../../../../../Images"
import { useDemoDataToggle } from "../../../../User/hooks/useDemoDataToggle"
import MonitoringBlockHeader from "../../../components/Monitoring/MonitoringBlockHeader/MonitoringBlockHeader"
import MonthCell from "../../../components/Monitoring/MonthCell/MonthCell"
import { getMonthCellLines } from "../../../components/Monitoring/monthCellLines"
import MonthStrip from "../../../components/Monitoring/MonthStrip/MonthStrip"
import { PROGRAMS } from "../../../components/Monitoring/programLimits"
import HealthActions from "../../../reducer"
import { useVampHistoricalDataMode } from "../../hooks/useHistoricalDataMode"
import useMonitoringProgramProgress from "../../hooks/useMonitoringProgramProgress"
import { useVampDataAvailable } from "../../hooks/useVampDataAvailable"
import { useVampDescriptor } from "../../hooks/useVampDescriptor"
import InfoLine from "../InfoLine/InfoLine"
import SectionHeader from "../SectionHeader/SectionHeader"
import MonitoringProgramEdges from "./MonitoringProgramEdges/MonitoringProgramEdges"
import ProgressBar from "./ProgressBar/ProgressBar"

import "./MonitoringProgram.less"

const statusMessageTextMap = {
  pending: null,
  compliant: "Your account is in good health and within all acceptable thresholds of ECM.",
  warning:
    "Your chargeback rate has exceeded the acceptable limit. If not addressed, you will enter the monitoring program.",
  under_monitoring:
    "You’re currently under monitoring. Reduce chargebacks to exit the program and avoid penalties.",
  no_status: null
}

const statusMessageVariantMap = {
  pending: "neutral",
  compliant: "success",
  warning: "warning",
  under_monitoring: "warning",
  no_status: "neutral"
}

const MonitoringProgram = ({ business_account_id }) => {
  const dispatch = useDispatch()
  const { isMobile } = useSelector((state) => state.nav)
  const { vampMonitoringProgramChart, vampMonitoringProgramChartFetching } = useSelector(
    (state) => state.health
  )
  const descriptor = useVampDescriptor()
  const data = vampMonitoringProgramChart ?? {}
  const currentStatus = data?.current_status
  const { isShowDemoData } = useDemoDataToggle()
  const { isHistoricalDataMode } = useVampHistoricalDataMode()
  const isVampDataAvailable = useVampDataAvailable()

  const yearOptions = useMemo(() => {
    const options = vampMonitoringProgramChart?.available_years ?? [new Date().getFullYear()]
    const years = []
    for (const y of options) {
      years.push({ label: y, value: y })
    }
    return years
  }, [vampMonitoringProgramChart])
  const [year, setYear] = useState(yearOptions?.[yearOptions.length - 1]?.value ?? null)

  useEffect(() => {
    if (!vampMonitoringProgramChart?.available_years?.includes(year)) {
      setYear(yearOptions?.[yearOptions.length - 1]?.value ?? null)
    }
  }, [yearOptions])

  useEffect(() => {
    if (descriptor && isVampDataAvailable) {
      dispatch(
        HealthActions.getVampMonitoringProgramChartRequest({
          business_account: business_account_id,
          year,
          descriptor,
          ...(isHistoricalDataMode ? { historical_data: true } : {})
        })
      )
    }
  }, [
    business_account_id,
    year,
    isShowDemoData,
    descriptor,
    isHistoricalDataMode,
    isVampDataAvailable
  ])

  const isLiveYear = data?.current_month?.year === year

  // The program window bar renders only while the account is in the program — the hook gates on
  // an under_monitoring status.
  const {
    isShowProgressBar,
    timeLineSegments,
    startDate,
    endDate,
    edgesIntervalWidth,
    edgesStartOffset,
    isFinishTextHidden
  } = useMonitoringProgramProgress({ data })

  return (
    <Flex vertical>
      <Spin
        wrapperClassName="monitoring-program-chart-spin"
        spinning={vampMonitoringProgramChartFetching}
      >
        <Flex
          gap={10}
          align="flex-start"
          justify="space-between"
          className="monitoring-program-header"
        >
          <div className="monitoring-program-header__main">
            <SectionHeader
              title="Visa Acquirer Monitoring Program (VAMP)"
              image={Images.visa_3d}
              marginBottom={8}
            />
            <MonitoringBlockHeader
              blockId="vamp"
              program={PROGRAMS.VAMP}
              status={currentStatus}
              currentMonth={data?.current_month}
              thresholds={data?.thresholds}
            />
          </div>
          <SelectComponent
            value={year}
            onChange={setYear}
            options={yearOptions}
            style={{ width: isMobile ? "100%" : 100 }}
            className="monitoring-program-year-select"
          />
        </Flex>

        {!isMobile && isShowProgressBar && (
          <Flex vertical gap={8} className="monitoring-program-window">
            <MonitoringProgramEdges
              intervalWidth={edgesIntervalWidth}
              startOffset={edgesStartOffset}
              isFinishTextHidden={isFinishTextHidden}
              startDate={startDate}
              endDate={endDate}
            />
            <Flex gap={4} style={{ width: "100%" }}>
              {timeLineSegments.map((segment, index) => (
                <ProgressBar
                  key={index}
                  progress={segment.progress}
                  style={{ width: segment.width }}
                />
              ))}
            </Flex>
          </Flex>
        )}

        <MonthStrip
          program={PROGRAMS.VAMP}
          timeline={data?.timeline}
          year={year}
          currentMonth={data?.current_month}
          thresholds={data?.thresholds}
          isMobile={isMobile}
          renderCell={(month, { popover }) => (
            <MonthCell
              key={month.month}
              month={month}
              lines={getMonthCellLines({ program: PROGRAMS.VAMP, month })}
              isCurrent={isLiveYear && month.month === data?.current_month?.month}
              popover={popover}
              isMobile={isMobile}
              isPrivate
            />
          )}
        />

        {statusMessageTextMap[currentStatus?.status] && (
          <InfoLine
            text={statusMessageTextMap[currentStatus?.status]}
            variant={statusMessageVariantMap[currentStatus?.status]}
            style={{ marginTop: "16px" }}
          />
        )}
      </Spin>
    </Flex>
  )
}

export default MonitoringProgram
